import { create } from 'zustand'
import { supabase } from '../lib/supabase'
import { checkLoginLimit } from '../utils/rateLimiter'
import { sanitizeEmail } from '../utils/sanitizer'
import { validatePassword } from '../utils/passwordValidator'
import Sentry from '../lib/sentry'

// Get app URL from environment variable or fallback to current origin
const getAppUrl = () => {
  return import.meta.env.VITE_APP_URL || window.location.origin
}

export const useAuthStore = create((set, get) => ({
  user: null,
  session: null,
  loading: true,
  userProfile: null,

  initialize: async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      set({ session, user: session?.user ?? null, loading: false })

      // Listen for auth changes
      supabase.auth.onAuthStateChange((_event, session) => {
        set({ session, user: session?.user ?? null })
      })
    } catch (error) {
      console.error('Auth initialization error:', error)
      set({ loading: false })
    }
  },

  loadUserProfile: async () => {
    const { user } = get()
    if (!user?.id) return
    try {
      const { data, error } = await supabase
        .from('users')
        .select('full_name, avatar_url, email')
        .eq('id', user.id)
        .single()
      if (!error && data) {
        set({ userProfile: data })
      }
    } catch (err) {
      console.error('Error loading user profile:', err)
    }
  },

  setUserProfile: (profile) => {
    set((state) => ({ userProfile: { ...state.userProfile, ...profile } }))
  },

  signUp: async (email, password, metadata = {}) => {
    try {
      // Sanitize and validate email
      const cleanEmail = sanitizeEmail(email)
      if (!cleanEmail) {
        throw new Error('Invalid email address')
      }

      // Validate password strength
      const passwordValidation = validatePassword(password)
      if (!passwordValidation.isValid) {
        throw new Error(passwordValidation.errors[0])
      }

      const { data, error } = await supabase.auth.signUp({
        email: cleanEmail,
        password,
        options: {
          data: metadata,
          emailRedirectTo: `${getAppUrl()}/#/dashboard`
        }
      })
      if (error) throw error
      return data
    } catch (error) {
      Sentry.captureException(error, {
        context: { function: 'signUp', email }
      })
      throw error
    }
  },

  signIn: async (email, password) => {
    try {
      // Check rate limiting
      await checkLoginLimit(email)

      // Sanitize email
      const cleanEmail = sanitizeEmail(email)
      if (!cleanEmail) {
        throw new Error('Invalid email address')
      }

      const { data, error } = await supabase.auth.signInWithPassword({
        email: cleanEmail,
        password
      })
      if (error) throw error
      set({ session: data.session, user: data.user })
      return data
    } catch (error) {
      Sentry.captureException(error, {
        context: { function: 'signIn', email }
      })
      throw error
    }
  },

  signOut: async () => {
    const { error } = await supabase.auth.signOut()
    if (error) throw error
    set({ session: null, user: null })
  },

  signInWithGoogle: async () => {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${getAppUrl()}/#/dashboard`
        }
      })
      if (error) throw error
      return data
    } catch (error) {
      Sentry.captureException(error, {
        context: { function: 'signInWithGoogle' }
      })
      throw error
    }
  },

  resetPassword: async (email) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${getAppUrl()}/#/reset-password`
    })
    if (error) throw error
  },

  updatePassword: async (newPassword) => {
    const { error } = await supabase.auth.updateUser({
      password: newPassword
    })
    if (error) throw error
  },
}))
